import React from 'react'
import { Box, Card, CardContent, Typography } from '@mui/material'
import { WhatsApp as WhatsAppIcon } from '@mui/icons-material'
import MetaEmbeddedSignupButton from '../../../features/MetaEmbeddedSignup/MetaEmbeddedSignupButton'
import { SMBData } from '../../../services/api'
import { usePaymentContext } from '../../../contexts/PaymentContext'
import { PendingPaymentBanner } from '../../../components/common/PendingPaymentBanner'

interface ConnectWhatsAppStateProps {
  smbId?: string;
  onCompleted: (response: { smb: SMBData }) => void;
}

export const ConnectWhatsAppState: React.FC<ConnectWhatsAppStateProps> = ({ smbId, onCompleted }) => {
  const { hasPendingCheckout } = usePaymentContext()

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '75vh', px: 2 }}>
      {hasPendingCheckout && (
        <PendingPaymentBanner sx={{ width: '100%', mt: 0, mb: 0 }} />
      )}

      {/* Card centered in remaining space */}
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Card
          sx={{
            maxWidth: { xs: '100%', sm: 480 },
            width: '100%',
            textAlign: 'center',
            borderRadius: { xs: 0, sm: '24px' },
            boxShadow: { xs: 'none', sm: '0 20px 40px -10px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.02)' },
            overflow: 'hidden',
            position: 'relative',
            bgcolor: { xs: 'transparent', sm: '#fff' },
          }}
        >
          {/* Subtle elegant background accent */}
          <Box
            sx={{
              position: 'absolute',
              top: 0, left: 0, right: 0,
              height: 160,
              background: 'radial-gradient(ellipse at top, #f1f5f9 0%, rgba(255,255,255,0) 70%)',
              pointerEvents: 'none'
            }}
          />
          <CardContent sx={{ py: 8, px: { xs: 4, sm: 6 }, position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 4, px: 2, py: 0.5, borderRadius: '9999px', background: '#eff6ff', color: '#2563eb', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px', border: '1px solid #bfdbfe' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3b82f6' }} />
              ONBOARDING
            </Box>
            
            <Box 
              sx={{ 
                width: 80, 
                height: 80, 
                borderRadius: '20px',
                bgcolor: '#fff',
                boxShadow: '0 10px 15px -3px rgba(15, 23, 42, 0.08), 0 4px 6px -4px rgba(15, 23, 42, 0.04), 0 0 0 1px rgba(15, 23, 42, 0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 4,
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': { 
                  transform: 'translateY(-2px)',
                  boxShadow: '0 20px 25px -5px rgba(15, 23, 42, 0.1), 0 8px 10px -6px rgba(15, 23, 42, 0.05), 0 0 0 1px rgba(15, 23, 42, 0.05)'
                }
              }}
            >
              <WhatsAppIcon sx={{ fontSize: 40, color: '#25D366' }} />
            </Box>
            
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em', mb: 1.5 }}>
              Connect WhatsApp
            </Typography>
            
            <Typography variant="body1" sx={{ color: '#475569', mb: 5, lineHeight: 1.6, px: { xs: 1, sm: 4 }, maxWidth: 480 }}>
              Connect your existing WhatsApp Business account, or create a new one to get started.
            </Typography>
            
            <MetaEmbeddedSignupButton
              smbId={smbId}
              onCompleted={onCompleted}
              buttonSx={{ 
                minWidth: 240, 
                py: 1.5, 
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)'
              }}
            />
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}
